import { Box, Flex } from '@chakra-ui/react'
import { useAuthContext } from 'context/AuthProvider'
import React, { useCallback, useRef } from 'react'
import { ChatMessage } from '../types'
import { useConversations } from '../useConversations'
import ChatPanel from './ChatPanel'
import ConversationList from './ConversationList'

interface ChatDrawerProps {
  isOpen: boolean
  onClose: () => void
}

const ChatDrawer: React.FC<ChatDrawerProps> = ({ isOpen, onClose }) => {
  const { isAuthenticated } = useAuthContext()
  const {
    conversations,
    activeConversation,
    isLoadingConversations,
    createConversation,
    selectConversation,
    deleteConversation,
    addMessage
  } = useConversations(!!isAuthenticated)

  const messages = activeConversation?.messages || []
  const messagesRef = useRef(messages)
  messagesRef.current = messages
  const addMessageRef = useRef(addMessage)
  addMessageRef.current = addMessage

  const setMessages = useCallback((updater: React.SetStateAction<ChatMessage[]>) => {
    if (typeof updater === 'function') {
      const currentMessages = messagesRef.current
      const newMessages = updater(currentMessages)
      const added = newMessages.filter(
        (m) => !currentMessages.find((existing) => existing.id === m.id)
      )
      added.forEach((msg) => addMessageRef.current(msg))
    }
  }, [])

  if (!isOpen) return null

  return (
    <Box
      position="fixed"
      bottom="24px"
      right="24px"
      zIndex={1001}
      w={{ base: 'calc(100vw - 48px)', sm: '400px' }}
      h={{ base: 'calc(100vh - 120px)', sm: '600px' }}
      maxH="80vh"
      boxShadow="2xl"
      borderRadius="lg"
      overflow="hidden"
      border="1px solid"
      borderColor="gray.200"
      bg="white"
    >
      <Flex direction="column" h="100%">
        <ConversationList
          conversations={conversations}
          activeId={activeConversation?.id || null}
          isLoading={isLoadingConversations}
          onSelect={selectConversation}
          onDelete={deleteConversation}
          onCreate={createConversation}
        />
        <Box flex={1} minH={0}>
          <ChatPanel
            messages={messages}
            setMessages={setMessages}
            onClose={onClose}
            showCloseButton={true}
            height="100%"
          />
        </Box>
      </Flex>
    </Box>
  )
}

export default ChatDrawer
